/**
 * T-001 SPIKE — Search-baseline probe.
 *
 * The hybrid track-changes engine assumes one thing: the string we diff against (the baseline) is
 * EXACTLY the string `Range.search` operates on — same characters, same handling of struck-through
 * (deleted) text, field markers, and control characters. If they differ even slightly, the
 * char-offset position pointer drifts and edits land in the wrong place. This module captures the
 * empirical facts needed to confirm (or refute) that assumption on a real redlined paragraph.
 *
 * It mutates nothing. It reads `paragraph.getRange('Whole').text`, runs a handful of `Range.search`
 * calls, and emits ONE structural summary through the real debug logger. Per the project rule, it
 * logs structure only — lengths, counts, offsets, char codes — never clause prose. The one text-ish
 * field it emits is a per-probe-word match count/positions, and the words are supplied by the
 * caller (so the operator controls what, if anything, is searched), plus a small fixed set of
 * whitespace/marker probes that carry no document content.
 *
 * Usage (from the Word task-pane dev console, with Debug logging on):
 *   window.__courtyardProbeSearchBaseline(79, ['Company', 'Schedule'])
 * then Copy logs and share the SEARCH_BASELINE_PROBE line.
 */

import { debugLog } from '../../utils/RemoteDebugLog';

/* global Word */

/** Control/marker characters worth checking for in range.text (field markers, LRM/RLM, tab, nbsp). */
const MARKER_CODES: Record<string, number> = {
  fieldBeginOrResult_0x13_0x14_0x15: 0x13, // field begin (0x13), separate (0x14), end (0x15)
  tab_0x09: 0x09,
  lineBreak_0x0b: 0x0b,
  nbsp_0x00a0: 0x00a0,
  lrm_0x200e: 0x200e,
  rlm_0x200f: 0x200f,
  objectAnchor_0xfffc: 0xfffc,
};

interface ProbeWordResult {
  /** sha-free, content-free descriptor: the operator chose this word; report only its length + counts. */
  wordLength: number;
  matchCount: number;
  /** start offsets of each match within range.text, if computable by indexOf (structural). */
  offsetsInRangeText: number[];
  /** whether every search match's text length equals the probe word length (sanity of match). */
  allMatchLengthsEqualWord: boolean;
}

/**
 * Run the probe. `probeWords` are supplied by the operator; only their length and match
 * counts/offsets are logged, never the words themselves.
 */
export async function probeSearchBaseline(
  paragraphIndex: number,
  probeWords: string[] = [],
): Promise<void> {
  try {
    await Word.run(async (context) => {
      const paras = context.document.body.paragraphs;
      paras.load('items');
      await context.sync();

      const para = paras.items[paragraphIndex];
      if (!para) {
        debugLog('[HybridSpike] SEARCH_BASELINE_PROBE: paragraph not found', {
          paragraphIndex,
          paragraphCount: paras.items.length,
        });
        return;
      }

      const whole = para.getRange('Whole');
      whole.load('text');
      // Tracked-change info, if the API version exposes it, to compare against range.text length.
      await context.sync();

      const rangeText = whole.text || '';

      // Which marker/control characters are present in range.text (structural — codes + counts only).
      const markersPresent: Record<string, number> = {};
      for (const [name, code] of Object.entries(MARKER_CODES)) {
        let n = 0;
        for (let i = 0; i < rangeText.length; i++) {
          if (rangeText.charCodeAt(i) === code) n++;
        }
        if (name.startsWith('fieldBeginOrResult')) {
          // count all three field control chars
          for (const c of [0x13, 0x14, 0x15]) {
            let m = 0;
            for (let i = 0; i < rangeText.length; i++) if (rangeText.charCodeAt(i) === c) m++;
            markersPresent['field_' + c.toString(16)] = m;
          }
        } else if (n > 0) {
          markersPresent[name] = n;
        }
      }

      // Per operator-supplied probe word: how many times Range.search finds it, and whether those
      // positions line up with indexOf in range.text (validates the position-pointer assumption).
      const probeResults: ProbeWordResult[] = [];
      for (const word of probeWords) {
        if (!word) continue;
        const results = (whole as unknown as {
          search: (q: string, o?: { matchCase?: boolean }) => Word.RangeCollection;
        }).search(word, { matchCase: false });
        results.load('items');
        await context.sync();
        const items = results.items || [];
        for (const it of items) (it as Word.Range).load('text');
        await context.sync();

        // offsets via indexOf on range.text (structural check that search-space == range.text)
        const offsets: number[] = [];
        let from = 0;
        for (;;) {
          const idx = rangeText.indexOf(word, from);
          if (idx === -1) break;
          offsets.push(idx);
          from = idx + Math.max(1, word.length);
        }
        const allEqual = items.every((it) => ((it as Word.Range).text || '').length === word.length);

        probeResults.push({
          wordLength: word.length,
          matchCount: items.length,
          offsetsInRangeText: offsets,
          allMatchLengthsEqualWord: allEqual,
        });
      }

      debugLog('[HybridSpike] SEARCH_BASELINE_PROBE', {
        paragraphIndex,
        rangeTextLength: rangeText.length,
        // first/last char codes so we can spot leading/trailing markers without logging text
        firstCharCode: rangeText.length ? rangeText.charCodeAt(0) : null,
        lastCharCode: rangeText.length ? rangeText.charCodeAt(rangeText.length - 1) : null,
        markersPresent,
        probeCount: probeResults.length,
        probes: probeResults,
        note:
          'Compare rangeTextLength against the OOXML accepted length and the full-visible length ' +
          'to determine whether range.text includes struck-through text. Compare matchCount/offsets ' +
          'against the diff position pointer to validate Nth-occurrence alignment.',
      });
    });
  } catch (err) {
    debugLog('[HybridSpike] SEARCH_BASELINE_PROBE: error', {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Expose the probe on window so it can be triggered from the task-pane dev console during the spike.
 * No UI; debug-only; safe to call repeatedly (reads only).
 */
export function installSearchBaselineProbe(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>).__courtyardProbeSearchBaseline = (
    paragraphIndex: number,
    probeWords?: string[],
  ) => probeSearchBaseline(paragraphIndex, probeWords ?? []);
}
